<template>
  <section class="development-detail-page">
    <PageHeader
        :back-route="{ name: 'user.my-development' }"
        :title="pageTitle"
        icon="uil-award"
    />

    <div v-if="loading" class="detail-state" role="status">
      <span uk-spinner="ratio: 0.8"></span>
      <span>{{ $t('general.loading') }}</span>
    </div>

    <div v-else-if="errorMessage || !assignment" class="detail-state detail-state--empty" role="alert">
      <i aria-hidden="true" class="uil-exclamation-circle"></i>
      <div>
        <strong>{{ $t('general.load_failed') }}</strong>
        <p>{{ errorMessage || $t('general.empty_list') }}</p>
      </div>
      <router-link :to="{ name: 'user.my-development' }" class="detail-back-button">
        {{ $t('general.my_development') }}
      </router-link>
    </div>

    <template v-else>
      <AlertMessage v-if="commentError" :message="{ type: 'error', text: commentError }" @remove="commentError = ''" />
      <AlertMessage v-if="commentSuccess" :message="{ type: 'success', text: commentSuccess }" @remove="commentSuccess = ''" />

      <AssignedCompetencyDetail :key="detailViewKey"
                            :assignment="assignment"
                            :can-comment="true"
                            :comments="comments"
                            :submitting="submittingComment"
                            viewer="employee"
                            @submit-comment="submitEmployeeComment"
      />
    </template>
  </section>
</template>

<script>
import PageHeader from "@/view/components/common/PageHeader.vue";
import AlertMessage from "@/view/components/notification/AlertMessage.vue";
import AssignedCompetencyDetail from "@/view/components/developmentCompetencies/AssignedCompetencyDetail.vue";
import ApiService from "@/core/services/api.service";
import widgetMixin from "@/core/mixin/widget.mixin";

export default {
  name: "MyDevelopmentDetail",
  mixins: [widgetMixin],
  components: {
    AlertMessage,
    AssignedCompetencyDetail,
    PageHeader,
  },
  data() {
    return {
      assignment: null,
      comments: [],
      loading: true,
      errorMessage: "",
      commentError: "",
      commentSuccess: "",
      submittingComment: false,
      detailViewKey: 0,
      requestSequence: 0,
    };
  },
  computed: {
    pageTitle() {
      return this.assignment?.name || this.$t("general.competency_detail");
    },
    userCompetencyId() {
      return this.$route.params.userCompetencyId;
    },
  },
  async mounted() {
    // Widget kapalıysa sayfa erişime kapalı (sol menü ile aynı kural)
    try {
      await this.getWidgetsMixin();

      if (!this.isMyCompetencyActiveMixin) {
        await this.$router.push({ path: "/dashboard" });
        return;
      }

      await this.loadDetail();
    } catch (error) {
      await this.$router.push({ path: "/dashboard" });
    }
  },
  watch: {
    userCompetencyId() {
      this.loadDetail();
    },
  },
  beforeDestroy() {
    this.requestSequence += 1;
  },
  methods: {
    async loadDetail() {
      const id = this.userCompetencyId;
      const requestId = ++this.requestSequence;
      this.assignment = null;
      this.comments = [];
      this.errorMessage = "";
      this.commentError = "";
      this.commentSuccess = "";
      this.submittingComment = false;

      if (!id) {
        this.errorMessage = this.$t("general.try_later");
        return;
      }

      this.loading = true;
      try {
        const response = await ApiService.get(`api/my-competencies/${id}`);
        if (requestId !== this.requestSequence) return;

        const item = response?.data?.data || response?.data;
        if (!item?.id) {
          this.errorMessage = this.$t("general.load_failed");
          return;
        }

        this.assignment = {
          id: item.id,
          name: item.name,
          description: item.description,
          assigned_at: item.assigned_at,
          updated_at: item.updated_at,
          comments_count: item.comments_count,
          latest_target: item.latest_target,
        };
        this.comments = Array.isArray(item.comments) ? item.comments : [];
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        this.errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            this.$t("general.load_failed");
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },
    async submitEmployeeComment(text) {
      if (this.submittingComment) return;

      const comment = String(text || "").trim();
      if (!comment) {
        this.commentError = this.$t("general.comment_required");
        return;
      }

      const targetCompetencyId = this.userCompetencyId;
      this.commentError = "";
      this.commentSuccess = "";
      this.submittingComment = true;

      try {
        await ApiService.post(
            `api/my-competencies/${targetCompetencyId}/comment`,
            { comment }
        );

        if (String(targetCompetencyId) !== String(this.userCompetencyId)) return;

        await this.loadDetail();
        this.detailViewKey += 1;
        this.commentSuccess = this.$t("general.comment_added_successfully");
      } catch (error) {
        if (String(targetCompetencyId) !== String(this.userCompetencyId)) return;
        this.commentError =
            error?.response?.data?.message ||
            error?.response?.data?.errors?.comment?.[0] ||
            error?.message ||
            this.$t("general.comment_add_failed");
      } finally {
        if (String(targetCompetencyId) === String(this.userCompetencyId)) {
          this.submittingComment = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.development-detail-page {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 8px 0 28px;
  box-sizing: border-box;
  overflow-x: hidden;
}

.detail-state {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  color: #64748b;
  text-align: center;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
}

.detail-state--empty {
  flex-direction: column;
}

.detail-state--empty > i {
  color: #94a3b8;
  font-size: 40px;
}

.detail-state strong {
  display: block;
  color: #334155;
}

.detail-state p {
  margin: 5px 0 0;
}

.detail-back-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 9px 14px;
  margin-top: 6px;
  border-radius: 8px;
  color: #fff;
  background: var(--backgroundColor, #666cff);
  text-decoration: none;
}

@media (max-width: 800px) {
  .development-detail-page {
    padding: 0 0px 20px;
  }

  .development-detail-page :deep(.page-header-block),
  .development-detail-page :deep(.page-header-block__left),
  .development-detail-page :deep(.page-header-block__title) {
    min-width: 0;
  }

  .development-detail-page :deep(.page-header-block) {
    gap: 8px;
  }

  .development-detail-page :deep(.page-header-block__title) {
    font-size: 18px;
  }

  .detail-state {
    width: 100%;
    min-height: 200px;
    box-sizing: border-box;
    padding: 28px 16px;
  }

  .detail-back-button {
    width: 100%;
    box-sizing: border-box;
    padding: 12px 16px;
  }
}
</style>
